import angular from 'angular';
import APIServiceModule from './APIService';
import analyticsServiceModule, {AnalyticsService} from './AnalyticsService';
import applicationCacheModule from './ApplicationCache';
import { createError } from '../error';
import { normalizeIdentifier, isFunction } from '../util';
import { castToDate, formatAsDateTimeStringWithoutTimezone } from '../dateutil';
import { INCREMENTAL_ENABLED } from "../constants";
import './';

/**
 * Provides a local data store for a single data type.
 *
 * The "Incremental" approach means that we keep track of the last-modified timestamp of each object, as provided by
 * the server. We can then provide a modified-since parameter in API calls so that we're only loading newly created or
 * modified records.
 *
 * Each IncrementalDataStore requires the following parameters to function:
 *
 *   - type (e.g. "Position")
 *   - path (API endpoint that returns a list of API records, relative to API base URL)
 *   - factory (function to call with a record from the API, to hydrate into a model)
 *   - idFieldOrCallback (string containing ID field name, or function that takes an API record and returns its ID)
 *   - lastModifiedFieldOrCallback (string containing last-modified field name, or a function that takes an API record
 *     and returns its last-modified date)
 *
 * Usage:
 *
 *     const store = $injector.instantiate(
 *       'IncrementalDataStore',
 *       'Position',
 *       'positions',
 *       apiRecord => createPositionModel(apiRecord),
 *       'id',
 *       'updated_at')
 *
 *     store.getAll()
 *       .then(allPositions => console.log("All positions:", allPositions);
 *
 *     store.getById(123)
 *       .then(position => console.log("Position 123:", position);
 *
 *     store.removeById(123)
 *
 *     store.setById(123, apiObjectOrPromise) // NOT YET IMPLEMENTED
 *
 *     store.destroy() // Delete cache and all references to objects
 *
 * Behind the scenes, we persist our state to ApplicationCache, which is backed by a local IndexedDB store.
 *
 * We use a single cache key per IncrementalDataStore, and all state is stored in the following structure:
 *
 *     {
 *         "last_update": "YYYY-MM-DD hh:mm:ss ZZZ",
 *         "ordered_ids": [ id1, id2, ... ],
 *         "api_records_by_id": {
 *           id1: { ... apiRecord ... },
 *           id2: { ... apiRecord ...},
 *           ...
 *         }
 *     }
 *
 * The private _populateFromCache() method reads all of this data, runs each apiRecord through the provided factory
 * method to create a hydrated object. The factory method returns a promise, and the promise is stored in a hash with
 * the key being the object ID:
 *
 *     {
 *         id => factoryPromise,
 *         ...
 *     }
 *
 */

const PERSIST_LOCAL_TIMEOUT = 100;

class IncrementalDataStore {
  constructor(
    APIService,
    AnalyticsService,
    LogService,
    ApplicationCache,
    DateTimeService,
    $timeout,
    $q
  ) {
    this.APIService = APIService;
    this.AnalyticsService = AnalyticsService;
    this.LogService = LogService;
    this.ApplicationCache = ApplicationCache;
    this.DateTimeService = DateTimeService;
    this.$timeout = $timeout;
    this.$q = $q;

    this._reset();
  }

  _reset() {
    this._updatePromise = null;
    this._restoreLocalDataOncePromise = null;
    this._needsPersistLocalData = false;
    this._persistTimeoutPromise = null;

    this._resetData();
  }

  _resetData() {
    this._lastApiUpdate = undefined;
    this._lastModifiedDate = null;
    this._lastModifiedObject = null;
    this._needsUpdate = true;
    this._apiRecordsById = {};
    this._orderedObjectIds = [];
    this._hydratedObjectPromisesById = {};
    this._orderedHydratedObjectPromises = null;

    this.invalidatePromises();
  }

  init(
    type = null,
    path = null,
    factory = null,
    idFieldOrCallback = null,
    lastModifiedFieldOrCallback = null,
    postUpdateCallback = null
  ) {
    this.type = type;
    this.path = path;
    this.factory = factory;
    this.idFieldOrCallback = idFieldOrCallback;
    this.lastModifiedFieldOrCallback = lastModifiedFieldOrCallback;
    this.postUpdateCallback = postUpdateCallback;
  }

  set type(type) {
    this._type = type;
  }

  get type() {
    return this._type;
  }

  set path(path) {
    this._path = path;
  }

  get path() {
    return this._path;
  }

  set factory(factory) {
    this._factory = factory;
  }

  get factory() {
    return this._factory;
  }

  set idFieldOrCallback(idFieldOrCallback) {
    this._idFieldOrCallback = idFieldOrCallback;
  }

  get idFieldOrCallback() {
    return this._idFieldOrCallback;
  }

  set lastModifiedFieldOrCallback(lastModifiedFieldOrCallback) {
    this._lastModifiedFieldOrCallback = lastModifiedFieldOrCallback;
  }

  get lastModifiedFieldOrCallback() {
    return this._lastModifiedFieldOrCallback;
  }

  set postUpdateCallback(cb) {
    this._postUpdateCallback = cb;
  }

  get postUpdateCallback() {
    return this._postUpdateCallback;
  }

  setNeedsUpdate() {
    this._needsUpdate = true;
  }

  invalidatePromises() {
    this._getAllPromise = null;
    this._getByIdPromiseMap = {};
    this._needsUpdate = true;
  }

  _dataSyncRefreshedPositions() {
    this.setNeedsUpdate();
  }

  _dataSyncRefreshedData() {
    this.setNeedsUpdate();
  }

  setNeedsPersistLocalData() {
    this._needsPersistLocalData = true;
    if (!this._persistTimeoutPromise) {
      this._persistTimeoutPromise = this.$timeout(() => {
        return this._persistLocalDataIfNeeded()
      }, PERSIST_LOCAL_TIMEOUT)
        .finally(() => {
          this._persistTimeoutPromise = null;
        });
    }
    return this.$q.when(true);
  }

  getAll(config = null) {
    return this.updateIfNeeded(config)
      .then(() => this.getAllLocal());
  }

  /**
   * Returns promise resolving to array of all hydrated objects that exist currently.
   * Does not perform an update.
   */
  getAllLocal() {
    const hydratedObjectPromises = this._orderedObjectIds
      .map(id => this._hydratedObjectPromisesById[id])
      .filter(promise => !!promise);
    return this.$q.all(hydratedObjectPromises);
  }

  getById(id, config = null) {
    const normId = normalizeIdentifier(id);
    return this.getAll(config)
      .then(() => {
        return this._hydratedObjectPromisesById[normId];
      });
  }

  getLocalById(id) {
    const normId = normalizeIdentifier(id);
    return this.getAllLocal()
      .then(hydratedObjects => hydratedObjects.find(o => this.idForObject(o) === normId));
  }

  removeById(id) {
    const normId = normalizeIdentifier(id);
    const filteredOrderedIds = this._orderedObjectIds.filter(id => id !== normId);
    let found = false;
    if (filteredOrderedIds.length !== this._orderedObjectIds.length) {
      this._orderedObjectIds = filteredOrderedIds;
      found = true;
    } else {
      this.LogService.warn(`IncrementalDataStore[${this.type}].removeById(${id}) did not find object`);
    }
    if (normId in this._hydratedObjectPromisesById) {
      found = true;
      delete this._hydratedObjectPromisesById[id];
    }
    if (normId in this._apiRecordsById) {
      found = true;
      delete this._apiRecordsById[id];
    }
    if (found) {
      this.setNeedsPersistLocalData();
    }
    return this.$q.when(found);
  }

  removeAll() {
    return this.$q.when(this._resetData());
  }

  idForObject(object) {
    if (isFunction(this.idFieldOrCallback)) {
      return normalizeIdentifier(this.idFieldOrCallback(object));
    }
    return normalizeIdentifier(object[this.idFieldOrCallback]);
  }

  idForApiRecord(data) {
    return this.idForObject(data);
  }

  lastModifiedForObject(object) {
    let lastModifiedValue;
    if (isFunction(this.lastModifiedFieldOrCallback)) {
      lastModifiedValue = this.lastModifiedFieldOrCallback(object);
    } else {
      lastModifiedValue = object[this.lastModifiedFieldOrCallback];
    }
    if (lastModifiedValue) {
      return castToDate(lastModifiedValue);
    } else {
      return null;
    }
  }

  update() {
    if (this._updatePromise) {
      return this._updatePromise;
    }

    let updatePromise;
    if (INCREMENTAL_ENABLED) {
      updatePromise = this._updateIncremental();
    } else {
      updatePromise = this._updateNonIncremental();
    }

    this._updatePromise = updatePromise
      .finally(result => {
        this._updatePromise = null;
        this._needsUpdate = false;
        return result;
      });

    return this._updatePromise;
  }

  _updateIncremental() {
    const config = {
      params: {},
      disableCache: true,
    };
    return this._restoreLocalDataOnce()
      .then(() => {
        // Determine if we need to include last modified date
        const useConfig = {...config};
        if (this._lastApiUpdate) {
          useConfig.params.modified_since = this._convertDateToServerLastModifiedString(this._lastApiUpdate);
        }
        return this.APIService.get(this.path, useConfig);
      })
      .then(response => this._hydrateObjectsFromApiResponse(response, config))
      .then(records => {
        return this.$q.when(this._markAsUpdatedNow())
          .then(() => records);
      })
      .then(records => this._applyPostUpdateCallback(records))
      .then(records => {
        return this.$q.when(this.setNeedsPersistLocalData())
          .then(() => records);
      });
  }

  _updateNonIncremental() {
    const config = { disableCache: true };
    return this.APIService.get(this.path, config)
      .then(response => {
        return this.removeAll()
          .then(() => response);
      })
      .then(response => this._hydrateObjectsFromApiResponse(response, config))
      .then(records => this._applyPostUpdateCallback(records));
  }

  _hydrateObjectsFromApiResponse(response, config = null) {
    if (!response || !response.data) {
      this.LogService.error(`IncrementalDataStore[${this.type}] update() response from ${this.path} has no data`);
      return this.$q.when([]);
    }
    const hydrateObjectPromises = response.data
      .map(apiRecord => this._hydrateApiRecord(apiRecord, config));
    return this.$q.all(hydrateObjectPromises);
  }

  _applyPostUpdateCallback(records) {
    if (this.postUpdateCallback) {
      this.$timeout(() => {
        return this.postUpdateCallback(records, this)
      });
    }
    return this.$q.when(records);
  }

  updateIfNeeded(config = null) {
    let needsUpdate = false;
    if (config && config.force) {
      needsUpdate = true;
    } else {
      needsUpdate = !!this._needsUpdate;
    }

    if (needsUpdate) {
      return this.update(config);
    }
    return this.$q.when(null);
  }

  destroy() {
    return this._removeLocalData()
      .then(() => {
        this._hydratedObjectPromisesById = null;
        this._reset();
        return true;
      });
  }

  /**
   * Private methods
   */

  _loadAllData() {
    return this._restoreLocaldataOnce()
      .then(() => this.updateIfNeeded());
  }

  _restoreLocalDataOnce() {
    if (!this._restoreLocalDataOncePromise) {
      this._restoreLocalDataOncePromise = this._restoreLocalData();
    }
    return this._restoreLocalDataOncePromise;
  }

  /**
   * Restore our state from ApplicationCache
   * @private
   */
  _restoreLocalData() {
    const cacheKey = this._cacheKey();

    // Load state from application cache
    return this.ApplicationCache.getItem(cacheKey)
      .then(cacheData => {
        if (cacheData) {
          const orderedObjectIds = cacheData.ordered_ids || [];
          const apiRecordsById = cacheData.api_records_by_id || {};
          const lastUpdate = this._deserializeLastUpdateTime(cacheData.last_update);

          // Hydrate all objects
          const hydratePromises = orderedObjectIds
            .map(objectId => apiRecordsById[objectId])
            .filter(apiRecord => !!apiRecord)
            .map(apiRecord => this._hydrateApiRecord(apiRecord));

          // Resolve all promises and then set last update
          return this.$q.all(hydratePromises)
            .then(hydratedObjects => {
              this._lastApiUpdate = lastUpdate;
              return hydratedObjects;
            });
        } else {
          return [];
        }
      });
  }

  _markAsUpdatedNow() {
    this._lastApiUpdate = new Date();
  }

  _serializeLastUpdateTime(dateObj) {
    const d = castToDate(dateObj);
    if (d) {
      return d.toISOString();
    }
    return null;
  }

  _deserializeLastUpdateTime(dateTimeString) {
    return castToDate(dateTimeString);
  }

  _convertDateToServerLastModifiedString(dateObj) {
    const d = castToDate(dateObj);
    let lastModifiedString = '';
    if (d) {
      lastModifiedString = this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(d);
    }
    return lastModifiedString;
  }

  /**
   * Write state to ApplicationCache
   * @private
   */
  _persistLocalData() {
    const cacheData = {
      last_update: this._serializeLastUpdateTime(this._lastApiUpdate),
      ordered_ids: this._orderedObjectIds,
      api_records_by_id: this._apiRecordsById,
    };
    const cacheKey = this._cacheKey();
    return this.ApplicationCache.setItem(cacheKey, cacheData)
      .then(() => {
        this._needsPersistLocalData = false;
      })
  }

  _persistLocalDataIfNeeded() {
    if (this._needsPersistLocalData) {
      return this._persistLocalData();
    }
    return this.$q.when(false);
  }

  _removeLocalData() {
    const cacheKey = this._cacheKey();
    return this.ApplicationCache.removeItem(cacheKey)
      .then(result => {
        return result;
      });
  }

  _cacheKey() {
    return `incremental:${this.type}`;
  };

  _loadRemoteData() {
    // Use shared promise to ensure max concurrency of one
    if (!this._loadRemoteDataPromise) {
      const config = {};
      this._loadRemoteDataPromise = this.APIService.get(this.path, config)
        .then(response => {
          if (!response || !response.data) {
            this.LogService.error(`IncrementalDataStore[${this.type}] _loadRemoteData() response from ${this.path} has no data`);
            return [];
          }
          const hydrateObjectPromises = response.data
            .map(apiRecord => this._hydrateApiRecord(apiRecord));
          return this.$q.all(hydrateObjectPromises);
        });
    }
    return this._loadRemoteDataPromise;
  }

  /**
   * Given an API record, call the appropriate factory method, which should return a promise.
   *
   * @param apiRecord
   * @param config
   * @returns {*}
   * @private
   */
  _hydrateApiRecord(apiRecord, config = null) {
    const id = this.idForApiRecord(apiRecord);
    const promise = this.factory(apiRecord, config)
      .then(hydratedObject => this._trackLastModified(hydratedObject));

    // Store promise for hydrated object
    this._hydratedObjectPromisesById[id] = promise;

    // Store API record
    this._apiRecordsById[id] = apiRecord;

    // Add ID to ordered array if not already present
    if (!this._orderedObjectIds.includes(id)) {
      this._orderedObjectIds.push(id);
    }

    return promise;
  }

  _trackLastModified(hydratedObject) {
    const lastModified = this.lastModifiedForObject(hydratedObject);
    if (lastModified > this._lastModifiedDate) {
      this._lastModifiedDate = lastModified;
      this._lastModifiedObject = hydratedObject;
    }
    return hydratedObject;
  }

}

const moduleName = 'recaps.services.IncrementalDataStore';
let module = angular.module(moduleName, [
  'recaps.services',
  APIServiceModule.name,
  analyticsServiceModule.name,
  applicationCacheModule.name,
]);

module.service('IncrementalDataStore', IncrementalDataStore);
module.factory('buildIncrementalDataStoreForType', ['$injector', 'IncrementalDataStore', ($injector, IncrementalDataStore) => {
  let numDataStoresBuilt = 0;
  return (type, path, factory, idFieldOrCallback = 'id', lastModifiedFieldOrCallback = 'updated_at', postUpdateCallback = null) => {
    const dataStore = $injector.instantiate(IncrementalDataStore.constructor);
    dataStore.init(
      type,
      path,
      factory,
      idFieldOrCallback,
      lastModifiedFieldOrCallback,
      postUpdateCallback
    );
    dataStore._num = numDataStoresBuilt++;
    return dataStore;
  };
}]);

export default module;
